/**
 * PDF autocontido, como o do protótipo: cada página é uma imagem JPEG
 * desenhada com as fontes do produto e embutida numa folha A4, sem biblioteca.
 * O arquivo só carrega o que a prévia mostrou — nada é recalculado aqui.
 */

export interface PdfPageImage {
  width: number;
  height: number;
  /** JPEG já codificado (`canvas.toDataURL("image/jpeg")` decodificado). */
  bytes: Uint8Array;
}

/** A4 em pontos, o tamanho que a imagem ocupa na página. */
const A4 = { width: 595.28, height: 841.89 };

export function jpegPagesToPdf(images: readonly PdfPageImage[]): Uint8Array {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [0];
  let size = 0;

  const add = (value: string | Uint8Array) => {
    const bytes = typeof value === "string" ? encoder.encode(value) : value;
    parts.push(bytes);
    size += bytes.length;
  };
  const object = (id: number, body: string) => {
    offsets[id] = size;
    add(`${id} 0 obj\n`);
    add(body);
    add("\nendobj\n");
  };

  add("%PDF-1.4\n");
  object(1, "<< /Type /Catalog /Pages 2 0 R >>");
  object(
    2,
    `<< /Type /Pages /Count ${images.length} /Kids [${images.map((_, index) => `${3 + index * 3} 0 R`).join(" ")}] >>`,
  );
  images.forEach((image, index) => {
    const id = 3 + index * 3;
    object(
      id,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4.width} ${A4.height}] /Resources << /XObject << /Im0 ${id + 1} 0 R >> >> /Contents ${id + 2} 0 R >>`,
    );
    offsets[id + 1] = size;
    add(
      `${id + 1} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.bytes.length} >>\nstream\n`,
    );
    add(image.bytes);
    add("\nendstream\nendobj\n");
    const stream = `q\n${A4.width} 0 0 ${A4.height} 0 0 cm\n/Im0 Do\nQ\n`;
    object(id + 2, `<< /Length ${encoder.encode(stream).length} >>\nstream\n${stream}endstream`);
  });

  const xref = size;
  add(`xref\n0 ${offsets.length}\n0000000000 65535 f \n`);
  for (const offset of offsets.slice(1)) add(`${String(offset).padStart(10, "0")} 00000 n \n`);
  add(`trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`);

  const out = new Uint8Array(size);
  let position = 0;
  for (const bytes of parts) {
    out.set(bytes, position);
    position += bytes.length;
  }
  return out;
}

/** Decodifica o `data:` de um canvas em bytes, para `jpegPagesToPdf`. */
export function dataUrlBytes(dataUrl: string): Uint8Array {
  const binary = atob(dataUrl.slice(dataUrl.indexOf(",") + 1));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}
